/**
 * Display formatting for the staffing module.
 *
 * BOOKING VOLUME IS NOT REVENUE. Every money figure produced here is trailing-12
 * month travel BOOKED by the customer — Andavo earns a fee slice of it. The
 * module's terminology rule (plan §7) is that those two must never read as the
 * same number, so volume is formatted with an explicit unit and the label
 * "booking volume" travels with it at every call site.
 */

/** $M with two decimals — the unit the whole model works in. */
export function volumeM(m: number): string {
  return `$${m.toFixed(2)}M`;
}

/** Whole dollars, for financial figures (loaded cost). Never used for volume. */
export function dollars(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

export function hours(n: number): string {
  return `${n.toLocaleString('en-US', { maximumFractionDigits: 1 })} hrs`;
}

export function ftes(n: number): string {
  return n.toFixed(2);
}

/**
 * A tier's floor, for display. `null` is the catch-all and must not render as
 * "$0.00M" — a floor of zero is a materially different rule from "everything
 * below", and 19 accounts in the reference snapshot sit at or under zero.
 */
export function tierFloor(minSpendM: number | null): string {
  return minSpendM === null ? 'everything below' : `${volumeM(minSpendM)}+`;
}

/**
 * Annual cost AT TARGET = base salary + the team-wide OTE incentive on it.
 *
 * On-target, not actual: the incentive is earned against performance, so this
 * is what the person costs when they hit target and not what was paid. Every
 * total built from it inherits that, which is why the UI says "at target"
 * rather than "payroll".
 *
 * MIRRORS `annualCostOf` in cm-api's `lib/staffing/roster.ts`. Restated rather
 * than imported because cm-api is npm-standalone and not importable from
 * apps/web — the same reason `csmKeyOf` is restated in `lib/model.ts` — and
 * pinned by a spec so the two cannot drift apart silently.
 *
 * `incentivePct` is on the 0-100 scale (10 = 10%), NOT a 0-1 fraction. Reading
 * it as a fraction is a 100x error in a salary figure.
 *
 * Null base → null cost, never 0. "We have not recorded what this person earns"
 * is not "this person is free", and the difference has to survive the
 * arithmetic or every uncosted hire silently joins payroll at zero.
 */
export function annualCost(
  baseSalaryAnnual: number | null | undefined,
  incentivePct: number | undefined,
): number | null {
  if (baseSalaryAnnual === null || baseSalaryAnnual === undefined) return null;
  if (!Number.isFinite(baseSalaryAnnual)) return null;
  const pct = Number.isFinite(incentivePct) ? (incentivePct as number) : 0;
  return Math.round(baseSalaryAnnual * (1 + pct / 100) * 100) / 100;
}
